import { useCallback, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { Notation } from "@/components/Notation";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import {
  TWINKLE_MELODY,
  flatten,
  melodyToSequence,
  melodyToMeasures,
  midiToPitchName,
  tonicMidiForPc,
} from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 3 — Twinkle Twinkle, Left Hand Bass.
 * Wrapped in a course-wide <KeyProvider> so every PianoDiagram, Notation
 * and audible example transposes when the student picks a different tonic.
 */

export default function PianoIntermediate3() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "3");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="Twinkle Twinkle — Left Hand Bass"
          exerciseGoal="A song you've known since childhood becomes a workout for your hand independence, your sense of harmony, and your ear for bass."
          bpm={76}
          difficulty="Intermediate"
        >
          <LessonBody />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

/* —— inner body has access to KeyContext —— */
function LessonBody() {
  const k = useKeyOrDefault();
  const [activePcs, setActivePcs] = useState<Set<number>>(new Set());
  const onMidiNote = useCallback((p: number) => {
    setActivePcs((prev) => {
      const next = new Set(prev);
      next.add(p);
      setTimeout(() => {
        setActivePcs((cur) => {
          const n = new Set(cur);
          n.delete(p);
          return n;
        });
      }, 300);
      return next;
    });
  }, []);

  // Roman-numeral roots for the current key
  const I  = k.pcFromDegree(1);
  const IV = k.pcFromDegree(4);
  const V  = k.pcFromDegree(5);
  const Iname  = k.nameForPc(I);
  const IVname = k.nameForPc(IV);
  const Vname  = k.nameForPc(V);

  return (
    <>
      {/* ───────────── STUDY DECK — sticky at top of the lesson ───────────── */}
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={[
            { degree: 1, tone: "root",  label: "1" },
            { degree: 2, tone: "voice", label: "2" },
            { degree: 3, tone: "voice", label: "3" },
            { degree: 4, tone: "voice", label: "4" },
            { degree: 5, tone: "voice", label: "5" },
          ]}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="The five bass notes of the lesson, lit on the keyboard"
        />
        <p className="mt-2 text-[12.5px] leading-snug text-cyan-300/70">
          Pick any tonic above. The keyboard, the staff and every example
          re-pitches in real time. Click any lit key to hear it.
        </p>
      </StudyDeck>

      {/* ───────────── 1 · WHY TWINKLE ───────────── */}
      <FadeIn>
        <Drill number="1" label="Set the scene" title="Yes, we're studying Twinkle Twinkle">
          <p>
            You may be thinking: <em>"I can kind of play the piano. Why is this
            teacher teaching me Twinkle Twinkle Little Star?"</em>
          </p>
          <p>
            Because the song you already know is the most powerful piece of
            equipment in your studio. The melody is on autopilot inside your
            body — which means your <strong>conscious attention</strong> is
            free to land somewhere else: on the bass, on your left hand, on
            the harmony, on the rhythmic seam between your two hands.
            Everything we're going to layer in this lesson lives in that
            freed-up space.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "The song is just stuck inside us, right? So it's always easy to
            build musical variations around something you always know, which
            you've kind of grown up with."
          </PIQuote>
          <p className="text-[14px] text-cyan-300/70">
            (And if you don't already know the tune — Jason's exact words —
            <em> "maybe you didn't go to school or something, right? Or I
            don't know, you're not from planet Earth."</em> Take a moment.)
          </p>
        </Drill>
      </FadeIn>

      {/* ───────────── 2 · HISTORY (animated timeline) ───────────── */}
      <FadeIn>
        <Drill label="One quick history" title="Why this melody travelled the world">
          <p>
            The melody we now call <em>Twinkle Twinkle</em> was first
            published in Paris in <strong>1761</strong> as the French tune{" "}
            <em>Ah! vous dirai-je, maman</em> ("Ah! shall I tell you,
            Mama"). Around <strong>1781–82</strong>, Mozart wrote{" "}
            <strong>Twelve Variations on "Ah, vous dirai-je, Maman"</strong>{" "}
            (catalogued today as <strong>K. 265</strong>) — and, as Jason
            jokes, some of those variations are crazy. In{" "}
            <strong>1806</strong>, the English poet Jane Taylor published the
            poem <em>The Star</em>; in <strong>1838</strong> her words and
            the French tune were printed together for the first time as{" "}
            <em>Twinkle, Twinkle, Little Star</em>. The same tune also
            carries the alphabet song.
          </p>
          <Timeline
            events={[
              { year: "1761", text: "Ah! vous dirai-je, maman published in Paris" },
              { year: "1781–82", text: "Mozart's Twelve Variations, K. 265", highlight: true },
              { year: "1806", text: "Jane Taylor's poem The Star" },
              { year: "1838", text: "Words and tune printed together as Twinkle, Twinkle, Little Star" },
            ]}
          />
        </Drill>
      </FadeIn>

      {/* ───────────── 3 · MELODY ON THE STAFF ───────────── */}
      <FadeIn>
        <Drill number="2" label="The melody" title="Twinkle on the staff">
          <p>
            Here's the whole melody, written around middle C in the key you've
            chosen above. Two-bar lines, six lines total. Press play to hear
            it transpose live.
          </p>
          <Notation
            measures={melodyToMeasures(TWINKLE_MELODY, tonicMidiForPc(k.tonicPc), 4, k.useFlats)}
            timeSig="4/4"
            width={1280}
            height={170}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={async () => {
                kickAudio();
                const seq = melodyToSequence(
                  flatten(TWINKLE_MELODY),
                  tonicMidiForPc(k.tonicPc),
                  76,
                  k.useFlats
                );
                await playSequence(seq, 76);
              }}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Play the melody in {k.tonicName}
            </button>
            <span className="self-center text-[12px] italic text-cyan-300/70">
              All six lines, slow tempo. No bass yet.
            </span>
          </div>
        </Drill>
      </FadeIn>

      {/* ───────────── 4 · THE BIG IDEA — Jason's bass line ───────────── */}
      <FadeIn>
        <Drill number="3" label="The big idea" title="Five bass notes hold the whole song up">
          <p>
            Jason's bass line for <em>Twinkle</em> uses only the first five
            notes of the scale — degrees <strong>1 2 3 4 5</strong>. In{" "}
            <strong>{k.tonicName} major</strong> those are{" "}
            <strong>{[1, 2, 3, 4, 5].map((d) => k.nameForPc(k.pcFromDegree(d))).join(" ")}</strong>.
            Look up at the master keyboard at the top of the page — those are
            the lit keys.
          </p>
          <PIInsight>
            <p>
              A bass note doesn't have to be a chord root. Under the melody's
              second "twinkle" the bass plays the 3rd ({k.nameForPc(k.pcFromDegree(3))})
              instead of the root — it still belongs to the tonic chord, and
              the bass line moves step by step instead of jumping.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      {/* ───────────── Note page 1 ───────────── */}
      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/03-twinkle-twinkle-left-hand-bass-page1.png"
          caption="Jason's chart — the bass notes for all six lines, with their scale degrees."
        />
      </FadeIn>

      {/* ───────────── 5 · STAGE 1 — bass under the song ───────────── */}
      <FadeIn>
        <Drill number="4" label="Stage 1" title="Four bass notes under each line">
          <p>
            Right hand keeps the melody. Left hand plays four bass notes per
            line — one every two beats — straight from Jason's chart. Click{" "}
            <strong>▶</strong> on any line to hear its bass in the current key.
          </p>
          <BassLineList
            lines={[
              { line: "Twinkle, Twinkle, little star", degrees: [1, 3, 4, 3] },
              { line: "How I wonder what you are",      degrees: [2, 1, 5, 1] },
              { line: "Up above the world so high",     degrees: [3, 2, 1, 5] },
              { line: "Like a diamond in the sky",      degrees: [3, 2, 1, 5] },
              { line: "Twinkle, Twinkle, little star",  degrees: [1, 3, 4, 3] },
              { line: "How I wonder what you are",      degrees: [2, 1, 5, 1] },
            ]}
          />
          <p className="mt-3">
            Your left hand never leaves the five-finger position. Your right
            hand stays in scale position. The mental work happens on the{" "}
            <em>seam</em> between them. Then add some expression — think of
            volume control, getting broader and louder, then breaking it down.
          </p>
        </Drill>
      </FadeIn>

      {/* ───────────── 7 · STAGE 3 — the thumb pedal ───────────── */}
      <FadeIn>
        <Drill number="5" label="Stage 2" title="The right-hand thumb trick">
          <p>
            The piano is not a powerful melodic instrument the way a violin or
            saxophone is — one piano note will never win against a flute or a
            violin. So we <strong>thicken the melody</strong>: the right-hand
            thumb adds a note under the melody, almost like a little alto part
            under the soprano.
          </p>
          <p>
            Keep the tonic ({Iname}) driving forward in the thumb under the
            melody. It works for pretty much every melody note — except where
            the melody itself is on {Iname}; there, add the {Vname} below
            instead.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={async () => {
                kickAudio();
                const tonicMidi = tonicMidiForPc(k.tonicPc);
                // melody as encoded
                const melody = melodyToSequence(
                  flatten(TWINKLE_MELODY),
                  tonicMidi,
                  76,
                  k.useFlats
                );
                // Thumb: the tonic under every melody note, except where the
                // melody is itself on the tonic — there, the fifth below
                // (lesson video: "Works pretty much for everything except
                // itself or maybe I can add a G there").
                let beat = 0;
                const pedal = flatten(TWINKLE_MELODY).map((n) => {
                  const thumb = n.interval % 12 === 0 ? tonicMidi - 5 : tonicMidi;
                  const ev = {
                    time: beat * (60 / 76),
                    notes: [midiToPitchName(thumb, k.useFlats)],
                    duration: n.beats >= 2 ? "2n" : "4n",
                    velocity: 0.55,
                  };
                  beat += n.beats;
                  return ev;
                });
                await playSequence([...melody, ...pedal], 76);
              }}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Hear it with the thumb in {k.tonicName}
            </button>
          </div>
        </Drill>
      </FadeIn>

      {/* ───────────── 8 · PLAY ALONG ───────────── */}
      <FadeIn>
        <Drill number="6" label="Play along" title="The whole arrangement, side by side">
          <p>
            Press play. Slow it to half speed while you're learning, then take
            it back up. The keyboard at the top of the page lights up live so
            you can match what your ear hears to what your hand needs to do.
          </p>
          <LessonMidi
            courseSlug="piano-intermediate"
            order="3"
            label={`Twinkle in ${k.tonicName} ${k.quality === "major" ? "major" : "minor"}`}
            originalBpm={76}
            onNote={onMidiNote}
          />
          {/* Live highlight on a small spotlight piano */}
          {activePcs.size > 0 && (
            <PianoDiagram
              octaves={2}
              startOctave={3}
              compact
              highlights={[...activePcs].map((p): KeyHighlight => ({ pc: p, tone: "voice" }))}
              ariaLabel="Live playback visualisation"
            />
          )}
        </Drill>
      </FadeIn>

      {/* ───────────── 9 · PRACTICE LADDER ───────────── */}
      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Climb four rungs in fifteen minutes">
          <PracticeLadder
            intro={
              <>
                Four rungs. Each one a specific job for your hands. Start the
                ladder, the timer runs, the rung you're on advances
                automatically. If your current rung wobbles, hit{" "}
                <em>Stay on this rung</em> and the ladder will hold there.
              </>
            }
            rungs={[
              { title: "Left hand alone",         minutes: 4, accent: "amber",   detail: `Jason's bass line only — ${[[1, 3, 4, 3], [2, 1, 5, 1], [3, 2, 1, 5], [3, 2, 1, 5], [1, 3, 4, 3], [2, 1, 5, 1]].map((line) => line.map((d) => k.nameForPc(k.pcFromDegree(d))).join(" ")).join(" | ")}. No melody. Feel the change.` },
              { title: "Right hand alone",        minutes: 4, accent: "cyan",    detail: "The melody, clean and in time. No bass, no thickening. Just the tune." },
              { title: "Both hands · Stage 1",    minutes: 4, accent: "rose",    detail: "Four bass notes per line, melody on top. Hands at different rates — that's the point." },
              { title: "Both hands · the thumb",  minutes: 3, accent: "emerald", detail: `Add the right-hand thumb: ${Iname} under the melody, ${Vname} where the melody is on ${Iname}. If it wobbles, drop back. Never push past clean.` },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ──────────────────────── helpers — local to this lesson ──────────────────────── */

/** Sticky study deck — collapsible, top of every PI lesson. */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
            ✦ Study deck
          </div>
          <div className="text-[12px] text-cyan-300/70">
            Key picker · master keyboard · always-visible while you read
          </div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        style={{ overflow: "hidden" }}
      >
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

/** Drop-in framer-motion fade-up for any block. */
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

/** Pull-quote, transcript-style. */
function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote
        className="text-[17px] leading-relaxed text-cyan-50/95"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}
      >
        {children}
      </blockquote>
      {attribution && (
        <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">
          — {attribution}
        </figcaption>
      )}
    </figure>
  );
}

/** Animated history timeline. */
function Timeline({ events }: { events: { year: string; text: string; highlight?: boolean }[] }) {
  return (
    <ol className="my-4 space-y-2">
      {events.map((e, i) => (
        <motion.li
          key={i}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.07, duration: 0.4 }}
          className="flex items-baseline gap-3"
        >
          <span
            className={`min-w-[3.5rem] rounded-md px-2 py-0.5 text-center font-mono text-[12px] font-bold ${
              e.highlight ? "bg-amber-400 text-zinc-900" : "bg-cyan-500/15 text-cyan-200"
            }`}
          >
            {e.year}
          </span>
          <span className="text-cyan-100/90">{e.text}</span>
        </motion.li>
      ))}
    </ol>
  );
}

/** Bass-line list (Jason's chart: four bass notes per line) with a click-to-hear button per line. */
function BassLineList({
  lines,
}: {
  lines: { line: string; degrees: number[] }[];
}) {
  const k = useKeyOrDefault();
  return (
    <ol className="space-y-2 text-[15px] text-cyan-50/95">
      {lines.map((l, i) => {
        const label = l.degrees.map((d) => k.nameForPc(k.pcFromDegree(d))).join(" ");
        return (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05, duration: 0.3 }}
            className="flex items-baseline gap-3 rounded-lg border border-cyan-500/10 bg-cyan-950/20 px-3 py-2"
          >
            <span className="inline-flex h-7 min-w-[6.5rem] items-center justify-center rounded-md bg-cyan-500/15 px-2 font-mono text-[13px] font-bold uppercase text-cyan-200">
              {label}
            </span>
            <span className="text-cyan-50/95">{l.line}</span>
            <button
              onClick={async () => {
                kickAudio();
                // Bass register sits an octave below middle C; degrees 1–5
                // stay inside one five-finger position above the tonic.
                const bassTonic = tonicMidiForPc(k.tonicPc) - 12;
                const noteForDegree = (deg: number): string => {
                  const targetPc = k.pcFromDegree(deg);
                  const semis = ((targetPc - k.tonicPc) % 12 + 12) % 12;
                  return midiToPitchName(bassTonic + semis, k.useFlats);
                };
                // One bass note every two beats (a half note) at 76 BPM.
                const BPM = 76;
                const half = (60 / BPM) * 2;
                await playSequence(
                  l.degrees.map((d, idx) => ({
                    time: idx * half,
                    notes: [noteForDegree(d)],
                    duration: "2n",
                  })),
                  BPM
                );
              }}
              className="ml-auto rounded-md border border-cyan-400/40 bg-cyan-500/10 px-2 py-1 text-[11px] font-semibold text-cyan-200 hover:bg-cyan-500/20"
            >
              ▶ hear bass
            </button>
          </motion.li>
        );
      })}
    </ol>
  );
}


